'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CircleX } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useDeviceType } from '@/hooks/useIsMobile';
import IconAdaptive from '@/components/IconAdaptive';

interface ModalSlideProps {
    onClose: () => void;
    isHeaderLogo?: boolean;
    children: React.ReactNode;
    pcWidth?: string;
    mbWidth?: string;
    height?: string;
    direction?: 'left' | 'right' | 'up' | 'down';
    zIndex?: number;
}

export default function ModalSlide({
    onClose,
    isHeaderLogo = false,
    children,
    pcWidth = '100%',
    mbWidth = '100%',
    height = '100%',
    direction = 'right',
    zIndex = 2000,
}: ModalSlideProps) {
    const { isMobile } = useDeviceType();
    const modalWidth = isMobile ? mbWidth : pcWidth;

    const isHorizontal = direction === 'left' || direction === 'right';
    const isLeft = direction === 'left';
    const isUp = direction === 'up';

    // 横スライドのときだけ左右に寄せる。縦スライド時は中央寄せにする
    const modalPosition = isHorizontal ? (isLeft ? 'justify-start' : 'justify-end') : 'justify-center';

    // 方向ごとに初期位置を決める
    let initialX: string | number = 0;
    let initialY: string | number = 0;

    switch (direction) {
        case 'left':
            initialX = '-100%';
            break;
        case 'right':
            initialX = '100%';
            break;
        case 'up':
            initialY = '-100%';
            break;
        case 'down':
            initialY = '100%';
            break;
        default:
            initialX = '100%';
    }

    const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget) {
            event.stopPropagation();
            onClose();
        }
    };

    // document.body の遅延取得
    const [modalContainer, setModalContainer] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setModalContainer(document.body);
    }, []);
    if (!modalContainer) return null;

    return createPortal(
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="fixed inset-0 flex items-start justify-start h-full"
            style={{ zIndex }}
        >
            <div className="absolute inset-0 bg-black/40" />
            <motion.div
                initial={{ x: initialX, y: initialY, opacity: 1 }} // ← y も追加
                animate={{ x: 0, y: 0, opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, type: 'tween', ease: 'easeInOut' }}
                className={`fixed inset-0 flex items-start ${modalPosition} h-full`}
                onClick={handleBackgroundClick}
            >
                <motion.div
                    className={
                        'flex flex-col bg-color-white overflow-hidden transform transition-all max-w-full h-full border-x border-color-base-1'
                    }
                    style={{ width: modalWidth, height }}
                >
                    <div className="fixed top-0 w-full bg-color-white-base z-10 h-16">
                        <div className="flex w-full my-2">
                            {isHeaderLogo ? (
                                <>
                                    <div className="w-[5%]"></div>
                                    <div className="flex flex-grow h-12">
                                        <div className="flex flex-grow justify-start items-center h-12">
                                            <div className="flex items-end cursor-pointer" onClick={() => onClose()}>
                                                <div className="font-nova font-bold text-color-base text-2xl lg:text-[30px] pl-2">
                                                    fox.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="flex flex-grow"></div>
                            )}
                            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-color-white-base">
                                <motion.div
                                    initial={false}
                                    whileHover={{ y: 2 }}
                                    className="p-2 rounded cursor-pointer group"
                                    onClick={() => onClose()}
                                >
                                    <IconAdaptive
                                        Icon={CircleX}
                                        strokeWidth={1.5}
                                        baseSize={30}
                                        className="text-color-base group-hover:text-color-service-mouseover"
                                    />
                                </motion.div>
                            </div>
                            <div className="w-[5%]"></div>
                        </div>
                    </div>
                    <div className="flex w-full h-full bg-color-white-base">
                        <div className="flex items-center justify-center w-full h-full bg-color-white-base">
                            <div className="flex justify-center items-start w-full h-full bg-color-white-base overflow-x-hidden overflow-y-hidden pt-16">
                                {children}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </motion.div>,
        modalContainer,
    );
}
